import qs from 'qs';

import { client } from '@api/client';
import { URL } from '@api/urls';

import { getTechnologyPartForUrl } from '@utils/getTechnologyPartForUrl';

import { IBandOption, ICell, TCellsData } from '@interfaces/cells/cells';
import {
	IAllCellsDTO,
	ICell4GDTO,
	ICell5GDTO,
	ICellIds,
	TCellWithDataDTO,
	TIdCellIdDto,
} from '@interfaces/cells/dto/cellsDTO';
import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { ITitle } from '@interfaces/common/common';
import { IIdDataDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';

import { TTechnologiesValues } from '@enums/common';

const {
	antennaModels,
	bands4GFdd,
	bands4GTdd,
	bands5G,
	cell4GFdd,
	cell4GTdd,
	cell5G,
	limenetCells,
	nokia,
	nokiaSites,
	sites,
	zones,
} = URL;

export const getAntennaModels = (): Promise<ITitle[]> => client.get(antennaModels);

export const getZones = (): Promise<ITitle[]> => client.get(zones);

export const getBands4GFdd = (): Promise<IBandOption[]> => client.get(bands4GFdd);

export const getBands4GTdd = (): Promise<IBandOption[]> => client.get(bands4GTdd);

export const getBands5G = (): Promise<IBandOption[]> => client.get(bands5G);

export const createCell4GFdd = ({
	id,
	data,
}: IIdDataDTO<ICell4GDTO>): Promise<void> => client.post(`${sites}/${nokia}/${id}/${cell4GFdd}`, data);

export const updateCell4GFdd = ({
	id,
	cellId,
	data,
}: TCellWithDataDTO<ICell4GDTO>): Promise<void> => client.put(`${sites}/${nokia}/${id}/${cell4GFdd}/${cellId}`, data);

export const createCell4GTdd = ({
	id,
	data,
}: IIdDataDTO<ICell4GDTO>): Promise<void> => client.post(`${sites}/${nokia}/${id}/${cell4GTdd}`, data);

export const updateCell4GTdd = ({ id, cellId, data }: TCellWithDataDTO<ICell4GDTO>): Promise<void> =>
	client.put(`${sites}/${nokia}/${id}/${cell4GTdd}/${cellId}`, data);

export const createCell5G = ({ id, data }: IIdDataDTO<ICell5GDTO>): Promise<void> =>
	client.post(`${sites}/${nokia}/${id}/${cell5G}`, data);

export const updateCell5G = ({ id, cellId, data }: TCellWithDataDTO<ICell5GDTO>): Promise<void> =>
	client.put(`${sites}/${nokia}/${id}/${cell5G}/${cellId}`, data);

export const get4GFddBySite = (id: string): Promise<IItemsResponse<ICell>> => {
	return client.get(`${sites}/${nokia}/${id}/${cell4GFdd}`);
};

export const get4GTddBySite = (id: string): Promise<IItemsResponse<ICell>> => {
	return client.get(`${sites}/${nokia}/${id}/${cell4GTdd}`);
};

export const get5GBySite = (id: string): Promise<IItemsResponse<ICell>> => {
	return client.get(`${sites}/${nokia}/${id}/${cell5G}`);
};

export const removeCells4GFdd = ({ id, cellIds }: ICellIds): Promise<void> =>
	client.delete(`${sites}/${nokia}/${cell4GFdd}/delete-many`, {
		params: {
			cellIds,
			siteId: id,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const removeCells4GTdd = ({ id, cellIds }: ICellIds): Promise<void> =>
	client.delete(`${sites}/${nokia}/${cell4GTdd}/delete-many`, {
		params: {
			cellIds,
			siteId: id,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const removeCells5G = ({ id, cellIds }: ICellIds): Promise<void> =>
	client.delete(`${sites}/${nokia}/${cell5G}/delete-many`, {
		params: {
			cellIds,
			siteId: id,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const removeCell4GFdd = ({ id, cellId }: TIdCellIdDto): Promise<void> =>
	client.delete(`${sites}/${nokia}/${id}/${cell4GFdd}/${cellId}`);

export const removeCell4GTdd = ({ id, cellId }: TIdCellIdDto): Promise<void> =>
	client.delete(`${sites}/${nokia}/${id}/${cell4GTdd}/${cellId}`);

export const removeCell5G = ({ id, cellId }: TIdCellIdDto): Promise<void> =>
	client.delete(`${sites}/${nokia}/${id}/${cell5G}/${cellId}`);

export const getAllCells4GFdd = (data: IAllCellsDTO): Promise<IItemsResponse<ISiteProfile>> =>
	client.get(`${sites}/${nokia}/${cell4GFdd}/filter`, {
		params: {
			...data,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const getAllCells4GTdd = (data: IAllCellsDTO): Promise<IItemsResponse<ISiteProfile>> =>
	client.get(`${sites}/${nokia}/${cell4GTdd}/filter`, {
		params: {
			...data,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const getAllCells5G = (data: IAllCellsDTO): Promise<IItemsResponse<ISiteProfile>> =>
	client.get(`${sites}/${nokia}/${cell5G}/filter`, {
		params: {
			...data,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const getLimenetCellsByCrowdcellId = (
	technology: TTechnologiesValues,
	crowdCellId?: string,
): Promise<TCellsData> => {
	const technologyPart = getTechnologyPartForUrl(technology);

	return client.get(`${limenetCells}/${technologyPart}/filter?crowdCellId=${crowdCellId}`);
};

export const getNokiaCellsBySiteId = (technology: TTechnologiesValues, siteId?: string): Promise<TCellsData> => {
	const technologyPart = getTechnologyPartForUrl(technology);

	return client.get(`${nokiaSites}/${siteId}/cells/${technologyPart}`);
};
